import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOnboarding } from '../context/OnboardingContext';
import { getMe } from '../api/auth';
import { Character, listCharacters, activateCharacter, startTeamImage } from '../api/characters';
import { getLevelUpJobStatus } from '../api/levelup';
import { Category, listCategories } from '../api/game';
import { getMyLeague, PlayerLeagueInfo } from '../api/leagues';
import CharacterCard from '../components/CharacterCard';
import CategoryCard from '../components/CategoryCard';
import Leaderboard from '../components/Leaderboard';
import OnboardingTooltip from '../components/OnboardingTooltip';
import '../styles/global.css';

const AccountHome: React.FC = () => {
  const { user, logout, refreshUser } = useAuth();
  const { shouldShowTooltip, dismissStep } = useOnboarding();
  const navigate = useNavigate();
  const location = useLocation();
  const [characters, setCharacters] = useState<Character[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [leagueInfo, setLeagueInfo] = useState<PlayerLeagueInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [activatingId, setActivatingId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [teamImageUrl, setTeamImageUrl] = useState<string | null>(null);
  const [teamImageLoading, setTeamImageLoading] = useState(false);

  // Onboarding refs
  const welcomeRef = useRef<HTMLDivElement>(null);
  const charactersRef = useRef<HTMLDivElement>(null);
  const coinsRef = useRef<HTMLParagraphElement>(null);
  const categoriesRef = useRef<HTMLDivElement>(null);
  const storeRef = useRef<HTMLButtonElement>(null);

  // Redirect to placement quiz if not completed
  useEffect(() => {
    if (user && !user.placement_completed) {
      navigate('/placement-quiz', { replace: true });
    }
  }, [user, navigate]);

  // Refetch data every time this page is navigated to
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Refresh user data (coins, etc.)
        await refreshUser();

        // Fetch characters fresh
        const data = await listCharacters();
        setCharacters(data);

        // Fetch league info
        try {
          const league = await getMyLeague();
          setLeagueInfo(league);
        } catch {
          // Ignore league fetch errors
        }

        // Check if active character has pending level-up
        const activeChar = data.find((c) => c.is_active);
        if (activeChar?.pending_level_up) {
          navigate(`/level-up/${activeChar.id}`, {
            state: { characterId: activeChar.id }
          });
        }

        // Team image: show existing or trigger generation
        // After refreshUser, read team_image_url from fresh /me
        const me = await getMe();
        if (me.team_image_url) {
          setTeamImageUrl(me.team_image_url);
        } else if (data.length > 0) {
          setTeamImageLoading(true);
          try {
            const { job_id } = await startTeamImage();
            // Poll for completion
            const poll = async () => {
              for (let i = 0; i < 60; i++) {
                await new Promise((r) => setTimeout(r, 2000));
                const status = await getLevelUpJobStatus(job_id);
                if (status.status === 'completed' && status.result) {
                  const url = (status.result as any).lineup_image_url;
                  if (url) {
                    setTeamImageUrl(url);
                    break;
                  }
                }
                if (status.status === 'failed') break;
              }
            };
            poll().finally(() => setTeamImageLoading(false));
          } catch {
            setTeamImageLoading(false);
          }
        }
      } catch (err) {
        setError('Failed to load characters');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [location.key]); // Refetch when location changes (navigation)

  useEffect(() => {
    const fetchCategories = async () => {
      setCategoriesLoading(true);
      try {
        const data = await listCategories();
        setCategories(data);
      } catch (err) {
        // Silently fail - categories are optional
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, [location.key]); // Refetch when location changes

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleActivate = async (characterId: string) => {
    setActivatingId(characterId);
    setError('');
    try {
      await activateCharacter(characterId);
      // Refresh character list to update is_active flags
      const data = await listCharacters();
      setCharacters(data);
    } catch (err) {
      setError('Failed to switch character');
    } finally {
      setActivatingId(null);
    }
  };

  const handlePlayGame = (categoryId: string) => {
    // Check if user has an active character
    const hasActiveCharacter = characters.some(c => c.is_active);
    if (!hasActiveCharacter) {
      setError('Please select a character first');
      return;
    }
    navigate(`/game/${categoryId}`);
  };

  const hasActiveCharacter = characters.some(c => c.is_active);
  const inLeague = !!user?.league_id;

  return (
    <div className="page">
      <nav className="navbar">
        <div className="nav-brand">Block Math</div>
        <div className="nav-links">
          <button ref={storeRef} className="nav-btn" onClick={() => navigate('/store')}>Go To Store</button>
          <button className="nav-btn nav-btn-secondary" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </nav>

      <div className="container">
        <div ref={welcomeRef} className="welcome-card">
          <h1>Welcome, {user?.username}!</h1>
          <p ref={coinsRef} className="coins">Coins: {user?.coins}</p>
        </div>

        {/* Team lineup image - only show if more than 1 character */}
        {characters.length > 1 && (teamImageLoading || teamImageUrl) && (
          <>
            <h2>My Team</h2>
            {teamImageLoading && (
              <div className="team-image-container">
                <div className="team-image-placeholder">
                  <div className="team-image-spinner" />
                  <span>Generating team lineup...</span>
                </div>
              </div>
            )}
            {teamImageUrl && !teamImageLoading && (
              <div className="team-image-container">
                <img src={teamImageUrl} alt="Team lineup" className="team-image" />
              </div>
            )}
          </>
        )}

        <div ref={charactersRef} className="characters-section">
          <h2>My Characters</h2>
          {error && <div className="error" style={{ marginTop: '10px' }}>{error}</div>}
          {loading ? (
            <p className="placeholder-text">Loading characters...</p>
          ) : characters.length === 0 ? (
            <p className="placeholder-text">No characters yet</p>
          ) : (
            <div className="character-grid">
              {characters.map((character) => (
                <CharacterCard
                  key={character.id}
                  character={character}
                  onActivate={handleActivate}
                  isActivating={activatingId === character.id}
                />
              ))}
            </div>
          )}
        </div>

        {/* Leaderboard or teaser */}
        {inLeague && leagueInfo?.league_id && leagueInfo?.league_name ? (
          <div style={{ marginBottom: '20px' }}>
            <Leaderboard
              leagueId={leagueInfo.league_id}
              leagueName={leagueInfo.league_name}
              currentPlayerId={user?.id}
            />
          </div>
        ) : !inLeague ? (
          <div className="card" style={{ marginBottom: '20px' }}>
            <h3>🏆 Leaderboard</h3>
            <p>Join a league to compete with friends and see how you rank!</p>
            <p style={{ fontSize: '0.9em', color: '#666' }}>
              Ask your parent or teacher to invite you to a league.
            </p>
          </div>
        ) : null}

        <div ref={categoriesRef} className="categories-section">
          <h2>Play a Game</h2>
          {!hasActiveCharacter && (
            <p className="warning-text">Select a character above to play</p>
          )}
          {categoriesLoading ? (
            <p className="placeholder-text">Loading categories...</p>
          ) : categories.length === 0 ? (
            <p className="placeholder-text">No categories available</p>
          ) : (
            <div className="category-grid">
              {categories.map((category) => (
                <CategoryCard
                  key={category.id}
                  category={category}
                  onPlay={handlePlayGame}
                  disabled={!hasActiveCharacter}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Onboarding tooltips */}
      {shouldShowTooltip('accountHome', 1) && (
        <OnboardingTooltip
          targetRef={welcomeRef}
          title="This is your home base!"
          message="Your characters, games, and scores all live here."
          onDismiss={() => dismissStep('accountHome', 1)}
        />
      )}
      {shouldShowTooltip('accountHome', 2) && (
        <OnboardingTooltip
          targetRef={charactersRef}
          title="Meet your characters!"
          message="Each character levels up as you play. The one with the green border is your active character."
          onDismiss={() => dismissStep('accountHome', 2)}
        />
      )}
      {shouldShowTooltip('accountHome', 3) && (
        <OnboardingTooltip
          targetRef={coinsRef}
          title="You earn coins by playing!"
          message="Spend them in the store to get new characters."
          onDismiss={() => dismissStep('accountHome', 3)}
        />
      )}
      {shouldShowTooltip('accountHome', 4) && (
        <OnboardingTooltip
          targetRef={categoriesRef}
          title="Pick a set to play!"
          message="Get a perfect score to earn a trophy. Collect enough trophies to master a set and unlock the next one!"
          onDismiss={() => dismissStep('accountHome', 4)}
        />
      )}
      {shouldShowTooltip('accountHome', 5) && (
        <OnboardingTooltip
          targetRef={storeRef}
          title="Want more characters?"
          message="Visit the store to spend your coins on new ones!"
          onDismiss={() => dismissStep('accountHome', 5)}
        />
      )}
    </div>
  );
};

export default AccountHome;
